import { ChevronLeft, ChevronRight, CalendarDays } from "lucide-react";
import { useMemo, useState } from "react";

const toDateKey = (date) => date.toISOString().split("T")[0];
const today = new Date();
today.setHours(0, 0, 0, 0);
const maxBookDate = new Date(today);
maxBookDate.setMonth(maxBookDate.getMonth() + 2);

const formatDisplayDate = (value) => {
  if (!value) return "Select date";
  return new Date(`${value}T12:00:00`).toLocaleDateString(undefined, { weekday: "short", month: "long", day: "numeric", year: "numeric" });
};

const getMonthDays = (monthDate) => {
  const year = monthDate.getFullYear();
  const month = monthDate.getMonth();
  const firstDay = new Date(year, month, 1);
  const startOffset = firstDay.getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  return [
    ...Array.from({ length: startOffset }, () => null),
    ...Array.from({ length: daysInMonth }, (_, index) => new Date(year, month, index + 1)),
  ];
};

export const CalendarDatePicker = ({ value, onChange, availabilityDays = [], label = "Appointment date", testId = "calendar-date-picker" }) => {
  const [open, setOpen] = useState(false);
  const [monthDate, setMonthDate] = useState(() => new Date(today.getFullYear(), today.getMonth(), 1));

  const availabilityMap = useMemo(() => {
    const map = new Map();
    availabilityDays.forEach((day) => {
      const availableWindows = day.windows?.filter((window) => window.status === "available").length || 0;
      map.set(day.date, { availableWindows, totalWindows: day.windows?.length || 0 });
    });
    return map;
  }, [availabilityDays]);

  const monthDays = getMonthDays(monthDate);
  const isCurrentMonth = monthDate.getFullYear() === today.getFullYear() && monthDate.getMonth() === today.getMonth();
  const nextMonth = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 1);
  const canGoNext = nextMonth <= maxBookDate;

  const selectDate = (date) => {
    const key = toDateKey(date);
    onChange(key);
    setOpen(false);
  };

  return (
    <div className="relative" data-testid={testId}>
      <button type="button" onClick={() => setOpen((current) => !current)} className="flex h-12 w-full items-center justify-between rounded-2xl border border-white/10 bg-white/[0.05] px-4 text-left text-white focus:outline-none focus:ring-2 focus:ring-blue-400" data-testid={`${testId}-trigger`}>
        <span data-testid={`${testId}-value`}>{formatDisplayDate(value)}</span>
        <CalendarDays className="h-4 w-4 text-zinc-400" />
      </button>
      {open && (
        <div className="absolute left-0 right-0 top-14 z-50 rounded-[1.5rem] border border-white/10 bg-zinc-950 p-4 shadow-2xl shadow-black/60" data-testid={`${testId}-popup`}>
          <div className="mb-4 flex items-center justify-between" data-testid={`${testId}-month-controls`}>
            <button type="button" disabled={isCurrentMonth} onClick={() => setMonthDate(new Date(monthDate.getFullYear(), monthDate.getMonth() - 1, 1))} className="rounded-full border border-white/10 p-2 text-white disabled:opacity-30" data-testid={`${testId}-prev-month`}><ChevronLeft className="h-4 w-4" /></button>
            <p className="font-display text-lg text-white" data-testid={`${testId}-month-label`}>{monthDate.toLocaleDateString(undefined, { month: "long", year: "numeric" })}</p>
            <button type="button" disabled={!canGoNext} onClick={() => setMonthDate(new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 1))} className="rounded-full border border-white/10 p-2 text-white disabled:opacity-30" data-testid={`${testId}-next-month`}><ChevronRight className="h-4 w-4" /></button>
          </div>
          <div className="grid grid-cols-7 gap-1 text-center text-xs uppercase tracking-[0.12em] text-zinc-500" data-testid={`${testId}-weekday-row`}>
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => <span key={day}>{day}</span>)}
          </div>
          <div className="mt-2 grid grid-cols-7 gap-1" data-testid={`${testId}-day-grid`}>
            {monthDays.map((date, index) => {
              if (!date) return <span key={`blank-${index}`} data-testid={`${testId}-blank-${index}`} />;
              const key = toDateKey(date);
              const outOfRange = date < today || date > maxBookDate;
              const availability = availabilityMap.get(key);
              const unavailable = availability && availability.availableWindows === 0;
              const selected = value === key;
              return (
                <button key={key} type="button" disabled={outOfRange || unavailable} onClick={() => selectDate(date)} className={`min-h-11 rounded-xl border p-1 text-sm transition-colors ${selected ? "border-blue-300 bg-blue-500/25 text-white" : unavailable ? "border-red-300/20 bg-red-400/10 text-red-100" : outOfRange ? "border-transparent text-zinc-700" : "border-white/10 bg-white/[0.04] text-zinc-200 hover:bg-white/[0.1]"}`} data-testid={`${testId}-date-button-${key}`}>
                  <span className="block" data-testid={`${testId}-day-number-${key}`}>{date.getDate()}</span>
                  {availability && !outOfRange && <span className="block text-[9px] uppercase tracking-wide text-zinc-500" data-testid={`${testId}-day-status-${key}`}>{unavailable ? "Full" : "Open"}</span>}
                </button>
              );
            })}
          </div>
          <p className="mt-3 text-xs text-zinc-500" data-testid={`${testId}-helper`}>{label}. Dates are limited to the next 2 months.</p>
        </div>
      )}
    </div>
  );
};